// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.

"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";

/**
 * Client wrapper for the `header` region. Owns the mobile-hamburger open
 * state and decides which viewport slot to show.
 *
 * Accessibility:
 * - The mobile panel is marked `role="dialog"` with `aria-modal="true"`
 *   and a labelled title (via `aria-labelledby` on the toggle).
 * - Escape closes the panel.
 * - When the panel opens, focus moves to the first interactive element
 *   inside it; when it closes, focus returns to the hamburger button so
 *   keyboard users don't lose their place.
 */
export function HeaderShell({
  brand,
  desktopRest,
  mobileRest,
}: {
  brand: ReactNode;
  desktopRest: ReactNode;
  mobileRest: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Collapse the panel whenever the route changes.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Escape closes the panel. Scoped to document so the key works anywhere.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Move focus into the panel on open, back to the toggle on close.
  useEffect(() => {
    if (open) {
      const firstInteractive = panelRef.current?.querySelector<HTMLElement>(
        'a, button, [tabindex]:not([tabindex="-1"])'
      );
      firstInteractive?.focus();
    } else if (toggleRef.current) {
      // Only restore focus if the toggle was the last focus-owner; avoid
      // stealing focus on initial mount (open=false from the start).
      // We detect "real close" by whether the panel ref has ever been
      // populated — on first render it's null so we skip.
      if (panelRef.current !== null) {
        toggleRef.current.focus();
      }
    }
  }, [open]);

  return (
    <nav className="w-full bg-stone-100 border-b-[3px] border-black sticky top-0 z-50">
      <div className="flex justify-between items-center gap-4 py-4 px-6">
        {brand}
        {/*
          `hidden md:contents` collapses this wrapper out of the flex
          tree at md+ so each `desktopRest` block (navLinks + ctaButton)
          becomes a direct flex sibling of brand and hamburger. With
          `justify-between` on the parent, they distribute across the bar.
        */}
        <div className="hidden md:contents">{desktopRest}</div>
        <button
          ref={toggleRef}
          type="button"
          className="md:hidden text-2xl p-2 -m-2"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="header-mobile-panel"
        >
          {open ? "✕" : "☰"}
        </button>
      </div>
      {open && (
        <div
          id="header-mobile-panel"
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          className="md:hidden px-6 py-4 border-t border-stone-300 flex flex-col gap-4"
          onClickCapture={(e) => {
            // Same-page anchor taps (e.g. /#services) don't change the
            // pathname so the useEffect above won't close — collapse on
            // any link click for safety.
            if ((e.target as HTMLElement).closest("a")) {
              setOpen(false);
            }
          }}
        >
          {mobileRest}
        </div>
      )}
    </nav>
  );
}
